import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './VMNetworkClassPanel.css';

const TIPOS = [
  { value: '', label: '— Não classificada —' },
  { value: 'internet', label: '🌐 Internet / IP válido' },
  { value: 'l2l', label: '🔗 L2L (interligação com o cliente)' },
  { value: 'interna', label: '🔒 Interna' },
];

export default function VMNetworkClassPanel({ vmid, onClose }) {
  const [interfaces, setInterfaces] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingIface, setSavingIface] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [agentIPs, setAgentIPs] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    setLoadError(false);
    try {
      const res = await axios.get(`/api/vms/${vmid}/network-class`);
      const ifs = res.data?.interfaces || [];
      const classifications = res.data?.classifications || [];
      setInterfaces(ifs);
      const f = {};
      for (const i of ifs) {
        const c = classifications.find(x => x.iface === i.iface);
        f[i.iface] = { tipo: c?.tipo || '', ip: c?.ip || '', observacao: c?.observacao || '' };
      }
      setForms(f);
    } catch (e) {
      setLoadError(true);
      setMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao carregar interfaces de rede.' });
    }
    setLoading(false);
    // IPs detectados pelo guest agent, casados por MAC (silencioso se agente ausente)
    try {
      const res = await axios.get(`/api/vms/${vmid}/agent/networks`);
      const map = {};
      for (const iface of res.data?.interfaces || []) {
        const mac = (iface.mac || '').toLowerCase();
        if (!mac) continue;
        const addrs = (iface.addresses || []).filter(a =>
          a.type === 'ipv4' && !(a.ip || '').startsWith('169.254.')
        );
        if (addrs.length > 0) map[mac] = addrs;
      }
      setAgentIPs(map);
    } catch (_) {}
  }, [vmid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (iface, field, value) => {
    setForms(prev => ({ ...prev, [iface]: { ...prev[iface], [field]: value } }));
  };

  const handleSave = async (iface) => {
    const f = forms[iface] || {};
    setSavingIface(iface);
    setMsg(null);
    try {
      if (!f.tipo) {
        await axios.put(`/api/vms/${vmid}/network-class`, { iface, clear: true });
        setMsg({ type: 'success', text: `Classificação de ${iface} removida.` });
      } else {
        await axios.put(`/api/vms/${vmid}/network-class`, {
          iface, tipo: f.tipo, ip: f.ip, observacao: f.observacao,
        });
        setMsg({ type: 'success', text: `${iface} salva.` });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao salvar classificação.' });
    }
    setSavingIface(null);
  };

  return (
    <div className="netclass-panel">
      <div className="netclass-panel-header">
        <span className="netclass-panel-title">🔌 Conexões de Rede — Classificação</span>
        <button className="netclass-close-btn" onClick={onClose}>×</button>
      </div>
      {loading ? (
        <div className="netclass-loading">Carregando interfaces...</div>
      ) : loadError ? null : interfaces.length === 0 ? (
        <div className="netclass-empty">Nenhuma interface de rede configurada nesta VM.</div>
      ) : (
        <div className="netclass-list">
          {interfaces.map(i => {
            const f = forms[i.iface] || { tipo: '', ip: '', observacao: '' };
            return (
              <div className="netclass-row" key={i.iface}>
                <div className="netclass-iface-info">
                  <span className="netclass-iface-name">{i.iface}</span>
                  <span className="netclass-iface-meta">{i.mac}</span>
                  {(agentIPs[(i.mac || '').toLowerCase()] || []).map(a => (
                    <button
                      key={a.ip}
                      type="button"
                      className="netclass-agent-ip"
                      title="IP detectado pelo guest agent — clique para usar no campo IP"
                      onClick={() => handleChange(i.iface, 'ip', a.ip)}
                      disabled={savingIface === i.iface}
                    >
                      {a.ip}{a.prefix != null ? `/${a.prefix}` : ''}
                    </button>
                  ))}
                </div>
                <select
                  className="netclass-select"
                  value={f.tipo}
                  onChange={e => handleChange(i.iface, 'tipo', e.target.value)}
                  disabled={savingIface === i.iface}
                >
                  {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  className="netclass-input"
                  placeholder="IP / faixa (ex.: 200.10.0.5 ou 10.0.0.0/30)"
                  value={f.ip}
                  onChange={e => handleChange(i.iface, 'ip', e.target.value)}
                  disabled={savingIface === i.iface}
                  maxLength={255}
                />
                <input
                  className="netclass-input netclass-obs"
                  placeholder="Observação (circuito, ponta do cliente...)"
                  value={f.observacao}
                  onChange={e => handleChange(i.iface, 'observacao', e.target.value)}
                  disabled={savingIface === i.iface}
                  maxLength={255}
                />
                <button
                  className="netclass-save-btn"
                  onClick={() => handleSave(i.iface)}
                  disabled={savingIface === i.iface}
                >
                  {savingIface === i.iface ? '...' : '✓ Salvar'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {msg && <div className={`netclass-msg ${msg.type}`}>{msg.text}</div>}
    </div>
  );
}
